import {
  ApplicationSectionStatus,
  RebateSmartCharging,
  SectionEvAccountInfoData,
  SectionEvChargerSelectionData,
  SectionEvInstallData,
  SectionEvPanelInfoData,
  SectionEvRebatesData,
  UpdateSectionEvAccountInfoInput,
  UpdateSectionEvChargerSelectionInput,
  UpdateSectionEvInstallInput,
  UpdateSectionEvPanelInfoInput,
  UpdateSectionEvRebatesInput,
} from '../../../__generated__/pgeplus-types';
import { getValue } from '../../../hooks/pge-plus/useRebatesEligibility';
import {
  ContactInfo,
  Installation,
  RebateEligibility,
  SelectCharger,
  UploadPhotos,
} from '../../pge-plus-form/evcharger.types';

export const contactInfoFormStateAdapter = () => ({
  toFormState: (sectionData: SectionEvAccountInfoData): ContactInfo => {
    return {
      email: sectionData.preferredContact?.emailAddress,
      firstName: sectionData.preferredContact?.firstName!,
      lastName: sectionData.preferredContact?.lastName!,
      middleName: sectionData.preferredContact?.middleName || '',
      phoneNumber: sectionData.preferredContact?.phoneNumber!,
      userRelationship: sectionData.preferredContact?.relationship!,
      homeType: sectionData.homeType!,
      isOwnHome: sectionData.isOwner === null ? undefined : sectionData.isOwner,
      isMainPerson:
        sectionData.useAccountContact === null
          ? undefined
          : sectionData.useAccountContact,
    };
  },
  fromFormState: (
    formData: ContactInfo,
    applictionId: string,
    sectionId: string,
  ): UpdateSectionEvAccountInfoInput => {
    return {
      id: sectionId,
      applicationId: applictionId,
      status: ApplicationSectionStatus.Completed,
      homeType: formData.homeType,
      isOwner: formData.isOwnHome,
      useAccountContact: formData.isMainPerson,
      preferredContact: {
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        emailAddress: formData.email,
        phoneNumber: formData.phoneNumber,
        relationship: formData.userRelationship,
      },
    };
  },
});

export const uploadPhotosFormStateAdapter = () => ({
  toFormState: (sectionData: SectionEvPanelInfoData): UploadPhotos => {
    return {
      panelImage: sectionData.panelImage!,
      amperageImage: sectionData.amperageImage!,
      panelLocationImage: sectionData.panelLocationImage!,

      extendedInstallDistance: sectionData.installDistance
        ?.extendedInstallDistance!,
      extendedInstallDistanceImage:
        sectionData.installDistance?.extendedInstallDistanceImage || '',

      amperageRating: sectionData.breakerInfo?.amperageRating!,
      doubleBreakerAvailable: sectionData.breakerInfo?.doubleBreakerAvailable!,

      chargerCost: sectionData.chargerCost!,
      receiptImage: sectionData.receiptImage!,
    };
  },
  fromFormState: (
    formData: UploadPhotos,
    applictionId: string,
    sectionId: string,
    saveAndComeBack: boolean = false,
  ): UpdateSectionEvPanelInfoInput => {
    return {
      id: sectionId,
      applicationId: applictionId,
      status: ApplicationSectionStatus.Completed,
      saveAndComeBack,
      breakerInfo: {
        amperageRating: formData.amperageRating || null,
        doubleBreakerAvailable: formData.doubleBreakerAvailable || null,
      },

      installDistance: {
        extendedInstallDistance: formData.extendedInstallDistance,
        extendedInstallDistanceImage: formData.extendedInstallDistanceImage,
      },

      amperageImage: formData.amperageImage,
      panelImage: formData.panelImage,
      panelLocationImage: formData.panelLocationImage,

      chargerCost: Number(formData.chargerCost),
      receiptImage: formData.receiptImage,
    };
  },
});

// NOTE: What the customer supplied (charger, vehicle).
export const pickChargerAndVehicleSelectionFields = (
  source: Pick<SelectCharger, 'modelNumber' | 'modelNumberImage' | 'vehicleManufacturer' | 'vehicleModel'>,
) => ({
  suppliedByCustomer: true as const,
  modelNumber: source?.modelNumber,
  modelNumberImage: source?.modelNumberImage,
  vehicleManufacturer: source?.vehicleManufacturer,
  vehicleModel: source?.vehicleModel,
});

// NOTE: What the backend validated. undefined means not yet validated. false means unqualified.
export const pickChargerValidationResultFields = (
  sectionData: SectionEvChargerSelectionData | undefined,
) => ({
  enrollmentPreference: sectionData?.enrollmentPreference || undefined,
  chargerIsQualified: sectionData?.chargerValidation
    ? sectionData.chargerValidation.isQualifiedProduct
    : undefined,
  vehicleIsQualified: sectionData?.vehicleValidation
    ? sectionData.vehicleValidation.isQualifiedVehicle
    : undefined,
  productName: sectionData?.chargerValidation?.product?.name,
});

export const selectChargerFormStateAdapter = () => ({
  toFormState: (sectionData: SectionEvChargerSelectionData): SelectCharger => {
    return {
      chargerId: sectionData.productId!,
      suppliedByCustomer: sectionData.suppliedByCustomer,
      modelNumber: sectionData.smartCharging?.modelNumber || undefined,
      modelNumberImage: sectionData.smartCharging?.modelNumberImage
        ? { id: (sectionData.smartCharging.modelNumberImage as any).id }
        : undefined,
      vehicleManufacturer: sectionData.vehicleManufacturerInfo?.vehicleManufacturer || undefined,
      vehicleModel: sectionData.vehicleManufacturerInfo?.vehicleModel || undefined,
      enrollmentPreference: sectionData.enrollmentPreference || undefined,
      chargerIsQualified: sectionData.chargerValidation
        ? sectionData.chargerValidation.isQualifiedProduct
        : undefined,
      vehicleIsQualified: sectionData.vehicleValidation
        ? sectionData.vehicleValidation.isQualifiedVehicle
        : undefined,
      productName: sectionData.chargerValidation?.product?.name,
    };
  },
  fromFormState: (
    formData: SelectCharger,
    applictionId: string,
    sectionId: string,
    saveAndComeBack: boolean = false,
  ): UpdateSectionEvChargerSelectionInput => {
    return {
      id: sectionId,
      applicationId: applictionId,
      status: ApplicationSectionStatus.Completed,
      productId: formData.chargerId,
      saveAndComeBack,
      smartCharging: formData.modelNumber
        ? {
            modelNumber: formData.modelNumber,
            modelNumberImage: formData.modelNumberImage,
          }
        : undefined,
      vehicleManufacturerInfo: formData.vehicleManufacturer
        ? {
            vehicleManufacturer: formData.vehicleManufacturer,
            vehicleModel: formData.vehicleModel || '',
          }
        : undefined,
      enrollmentPreference: formData.enrollmentPreference,
    };
  },
});

const getRebatesSectionOptionDataValue = (value: any) =>
  value === true ? 'Yes' : value === false ? 'No' : undefined;

export const evRebatesFormStateAdapter = () => ({
  toFormState: (sectionData: SectionEvRebatesData): RebateEligibility => {
    return {
      isNeedRebateInfo: getRebatesSectionOptionDataValue(
        sectionData?.iqdb?.checkEligibility,
      ),
      houseHoldSize: sectionData?.iqdb?.houseHoldSize
        ? `${sectionData?.iqdb?.houseHoldSize}`
        : undefined,
      annualHouseholdIncome: sectionData?.iqdb?.grossIncome
        ? `${sectionData?.iqdb?.grossIncome}`
        : undefined,

      isAnnnualIncomeChecked: sectionData?.iqdb?.grossIncomeConfirmed
        ? 'true'
        : undefined,
      isHaveWifi: getRebatesSectionOptionDataValue(
        sectionData?.smartCharging?.wifiAvailable,
      ),
      isOwnTesla: getRebatesSectionOptionDataValue(
        sectionData?.evPulse?.connectEVPulseApp,
      ),
      isCommitToExtendWifi: getRebatesSectionOptionDataValue(
        sectionData?.smartCharging?.activateChargerInApp,
      ),
      chargerForExistingVehicle:
        getRebatesSectionOptionDataValue(
          sectionData?.evPulse?.chargerForExistingVehicle,
        ) ||
        getRebatesSectionOptionDataValue(
          sectionData?.smartCharging?.chargerForExistingVehicle,
        ),

      continueWithoutRebate:
        getRebatesSectionOptionDataValue(
          sectionData?.evPulse?.continueWithoutRebate,
        ) ||
        getRebatesSectionOptionDataValue(
          sectionData?.smartCharging?.continueWithoutRebate,
        ),
      vin: sectionData?.evPulse?.vin || undefined,

      vinImage: sectionData?.evPulse?.vinImage || undefined,

      amount: sectionData?.rebateEligibility?.eligible
        ? (sectionData?.rebateEligibility
            ?.evRebate as RebateSmartCharging)?.charger?.toString() ||
          (sectionData?.rebateEligibility
            ?.evRebate as RebateSmartCharging)?.amount?.toString() ||
          '0'
        : '0',
    };
  },
  fromFormState: (
    formData: RebateEligibility,
    applictionId: string,
    sectionId: string,
  ): UpdateSectionEvRebatesInput => {
    return {
      id: sectionId,
      applicationId: applictionId,
      status: ApplicationSectionStatus.Completed,
      evPulse: {
        chargerForExistingVehicle: getValue(
          formData?.chargerForExistingVehicle,
        ),
        connectEVPulseApp: getValue(formData?.isOwnTesla),
        continueWithoutRebate: getValue(formData?.continueWithoutRebate),
        vin: formData?.vin,
        vinImage: formData?.vinImage,
      },
      iqdb: {
        checkEligibility: getValue(formData?.isNeedRebateInfo),
        grossIncome: formData?.annualHouseholdIncome
          ? Number(formData?.annualHouseholdIncome)
          : undefined,
        grossIncomeConfirmed: formData?.isAnnnualIncomeChecked === 'true',
        houseHoldSize: formData?.houseHoldSize
          ? Number(formData?.houseHoldSize)
          : undefined,
      },
      smartCharging: {
        chargerForExistingVehicle: getValue(
          formData?.chargerForExistingVehicle,
        ),
        continueWithoutRebate: getValue(formData?.continueWithoutRebate),
        wifiAvailable: getValue(formData?.isHaveWifi),
        activateChargerInApp: getValue(formData?.isCommitToExtendWifi),
      },
    };
  },
});

export const installationFormStateAdapter = () => ({
  toFormState: (sectionData: SectionEvInstallData): Installation => {
    return {
      dayOfWeek: sectionData.schedulePreference?.dayOfWeek!,
      preferredServiceTime: sectionData.schedulePreference
        ?.preferredServiceTime!,

      panelLocation: sectionData.panelLocation?.location!,
      comment: sectionData.panelLocation?.comment!,
      installLocation: sectionData.chargerInstallPreference?.installLocation!,

      installerRecommendPanelDistance: sectionData.chargerInstallPreference
        ?.installerRecommendPanelDistance!,
      distanceToPanel: sectionData.chargerInstallPreference?.distanceToPanel!,
      panelSideLocation: sectionData.chargerInstallPreference
        ?.panelSideLocation!,
    };
  },
  fromFormState: (
    formData: Installation,
    applictionId: string,
    sectionId: string,
    saveAndComeBack: boolean = false,
  ): UpdateSectionEvInstallInput => {
    return {
      id: sectionId,
      applicationId: applictionId,
      status: ApplicationSectionStatus.Completed,
      saveAndComeBack,

      schedulePreference: {
        urgency: formData.urgency,
        dayOfWeek: formData.dayOfWeek,
        preferredServiceTime: formData.preferredServiceTime,
      },
      panelLocation: {
        comment: formData.comment,
        location: formData.panelLocation,
      },
      chargerInstallPreference: {
        distanceToPanel: formData.distanceToPanel,
        installLocation: formData.installLocation,
        installerRecommendPanelDistance: !!formData.installerRecommendPanelDistance,
        panelSideLocation: formData.panelSideLocation,
      },
    };
  },
});
